(ns io.daskul.webapp.widgets.dashboard.stat-card
  "Stat card widget — renders a single metric with optional trend indicator.

   Expects a metric map with :widget :stat shape:
     {:widget :stat
      :key    \"active-students\"
      :label  \"Active Students\"
      :value  142
      :previous-value 128        ;; optional — enables trend arrow
      :format :number}           ;; :number | :currency | :percent"
  (:require
   [uix.core :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.format :as format]
   [io.daskul.webapp.ui.icons :as icons]))

(defn- format-value
  "Format a metric value based on :format type."
  [value fmt]
  (case fmt
    ;; Whole units: a stat card has no room for centavos, and the symbol comes
    ;; from the same money config the forms and list columns read.
    :currency (format/format-currency (or value 0) {:decimals 0})
    :percent  (str (.toFixed (* 100 value) 1) "%")
    ;; :number or default
    (str value)))

(defn- trend-direction
  "Returns :up, :down, or :neutral based on current vs previous value."
  [value previous-value]
  (cond
    (> value previous-value) :up
    (< value previous-value) :down
    :else                    :neutral))

(defn- trend-color [direction]
  (case direction
    :up   "teal"
    :down "red"
    "gray"))

(defn- trend-icon [direction]
  (case direction
    :up   icons/icon-trending-up
    :down icons/icon-trending-down
    icons/icon-minus))

(defui stat-card
  "Renders a stat metric as a card with label, formatted value, optional trend,
   and optional description tooltip. When :href is provided, the card is clickable."
  [{:keys [label value previous-value format href description caption pill
           value-color caption-color]}]
  (let [[hovered? set-hovered!] (uix.core/use-state false)
        fmt           (keyword (or format :number))
        has-trend?    (some? previous-value)
        direction     (when has-trend? (trend-direction value previous-value))
        trend-ic      (when has-trend? (trend-icon direction))
        color         (when has-trend? (trend-color direction))
        delta-pct     (when (and has-trend? (number? previous-value) (not (zero? previous-value)))
                        (js/Math.round (* 100 (/ (- value previous-value) previous-value))))
        content       ($ ui/paper {:p "lg" :radius "md" :withBorder true
                                   :onMouseEnter (when href #(set-hovered! true))
                                   :onMouseLeave (when href #(set-hovered! false))
                                   ;; height 100% so cards in a stretched grid row
                                   ;; are equal height regardless of caption/delta.
                                   :style (cond-> {:height "100%"}
                                            href (assoc :cursor "pointer"
                                                        :transition "box-shadow 150ms ease, transform 150ms ease"
                                                        :transform (when hovered? "translateY(-2px)")
                                                        :boxShadow (when hovered? "0 4px 12px rgba(0,0,0,0.08)")))}
                        ;; Label + info icon sit together (icon beside the title,
                        ;; not pinned to the far edge) so the hint reads as part
                        ;; of the label.
                        ($ ui/group {:gap 6 :align "center" :wrap "nowrap" :mb 4}
                          ($ ui/text {:size "xs" :tt "uppercase" :fw 600 :c "dimmed"}
                            label)
                          (when description
                            ($ ui/tooltip {:label description :multiline true :w 220
                                           :position "top-end" :withArrow true}
                              ($ ui/text {:c "dimmed" :style #js {:cursor "help"} :lh 1}
                                ($ icons/icon-info-circle {:size 14 :stroke 1.5})))))
                        ($ ui/group {:justify "space-between" :align "flex-end" :gap "xs"}
                          ($ ui/text {:size "1.75rem" :fw 700 :lh 1.2
                                      :style {:fontFamily "Sora, sans-serif"
                                              :fontVariantNumeric "tabular-nums"
                                              ;; colour via style, not :c — a nil
                                              ;; :c is JS null, which Mantine's
                                              ;; colour parser rejects as "object".
                                              :color (cond
                                                       value-color
                                                       (str "var(--mantine-color-" value-color "-6)")
                                                       ;; negative money in red — the
                                                       ;; accounting convention; a loss
                                                       ;; shouldn't read like a gain.
                                                       (and (= fmt :currency) (number? value) (neg? value))
                                                       "var(--mantine-color-red-6)")}}
                            (format-value value fmt))
                          (when href
                            ($ ui/text {:c "dimmed" :lh 1}
                              ($ icons/icon-chevron-right {:size 16 :stroke 1.5}))))
                        ;; Delta line: "▲ 14% vs prev" (colored), below the value.
                        (when has-trend?
                          ($ ui/group {:gap 3 :align "center" :wrap "nowrap" :mt 4}
                            ($ ui/text {:c color :lh 1} ($ trend-ic {:size 14 :stroke 2}))
                            ($ ui/text {:size "xs" :c color :fw 600}
                              (if delta-pct (str (js/Math.abs delta-pct) "% vs prev") "vs prev"))))
                        ;; Bottom line: an optional colored caption (e.g. red
                        ;; "₱X overdue") next to an optional "as of today" pill.
                        (when (or caption pill)
                          ($ ui/group {:gap 6 :align "center" :wrap "wrap" :mt 4}
                            (when caption
                              ($ ui/text {:size "xs" :c (or caption-color "dimmed")} caption))
                            (when pill
                              ($ ui/badge {:variant "light" :color "gray" :size "sm" :radius "sm"}
                                pill)))))]
    (if href
      ($ ui/anchor {:href href :underline "never" :c "inherit"} content)
      content)))
